import {
    useContext,
    useEffect
} from "react";
import {
    useWindowDimensions,
    ScrollView,
    Image,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    LocaleSwitcher,
    ThemeSwitcher,
    UserShortcut,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useNavigation
} from "@react-navigation/native";
import type RootStackParamList from "../../navigation/type";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    GraduationCap,
    ShieldCheck,
    Wallet
} from "lucide-react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
    AuthContext
} from "../../contexts/AuthContext";
import InstallPwa from "../../components/installPwa";

const Landing = () => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        activeTheme,
        radiuses,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        isAuthenticated,
        logout,
        user
    } = useContext(AuthContext);

    useEffect(() => {
        const checkRedirect = async () => {
            const shouldRedirect = await AsyncStorage.getItem("redirect_to_login");
            if (shouldRedirect === "true") {
                await AsyncStorage.removeItem("redirect_to_login");
                navigation.navigate("Login");
            }
        };
        checkRedirect();
    }, [navigation]);

    const handlePanelPress = () => {
        if(!isAuthenticated) {
            navigation.navigate("Login");
            return;
        }

        navigation.navigate("AuthenticatedApp" as never);
    };

    const renderHero = () => {
        return <View
            style={stylesheet.heroContainer}
        >
            <View
                style={stylesheet.heroTextContainer}
            >
                <Image
                    source={{
                        uri: activeTheme.indexOf("dark") !== -1 ? "https://dergah.app/assets/images/darklogo.png" : "https://dergah.app/assets/images/lightlogo.png"
                    }}
                    style={stylesheet.logo}
                    resizeMode="contain"
                />
                <Text
                    style={stylesheet.heroTitle}
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("landing-hero-title")}
                </Text>
                <Text
                    style={stylesheet.heroSubtitle}
                    variant="bodyLargeSize"
                    color="mid"
                >
                    {localize("landing-hero-subtitle")}
                </Text>
            </View>
            <View
                style={[
                    stylesheet.heroButtonsContainer,
                    {
                        width: isMobile ? "100%" : undefined
                    }
                ]}
            >
                <Button
                    spreadBehaviour={isMobile ? "stretch" : "free"}
                    title={localize("landing-get-started-button")}
                    onPress={handlePanelPress}
                    variant="filled"
                    size="medium"
                />
                <InstallPwa
                    spreadBehaviour={isMobile ? "stretch" : "free"}
                />
            </View>
        </View>;
    };

    const renderFeatures = () => {
        return <View
            style={stylesheet.featuresContainer}
        >
            <View
                style={[
                    stylesheet.featureCard,
                    {
                        backgroundColor: colors.content.container.default,
                        borderColor: colors.content.border.subtle,
                        width: isMobile ? "100%" : undefined,
                        borderRadius: radiuses.md
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.featureIconContainer,
                        {
                            backgroundColor: colors.system.default.background,
                            borderRadius: radiuses.md
                        }
                    ]}
                >
                    <ShieldCheck
                        color={colors.content.icon.default}
                        size={28}
                    />
                </View>
                <View
                    style={stylesheet.featureTextContainer}
                >
                    <Text
                        variant="titleSmallSize"
                        color="high"
                    >
                        {localize("landing-feature-1-title")}
                    </Text>
                    <Text
                        style={stylesheet.featureDescription}
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {localize("landing-feature-1-desc")}
                    </Text>
                </View>
            </View>
            <View
                style={[
                    stylesheet.featureCard,
                    {
                        backgroundColor: colors.content.container.default,
                        borderColor: colors.content.border.subtle,
                        width: isMobile ? "100%" : undefined,
                        borderRadius: radiuses.md
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.featureIconContainer,
                        {
                            backgroundColor: colors.system.default.background,
                            borderRadius: radiuses.md
                        }
                    ]}
                >
                    <Wallet
                        color={colors.content.icon.default}
                        size={28}
                    />
                </View>
                <View
                    style={stylesheet.featureTextContainer}
                >
                    <Text
                        variant="titleSmallSize"
                        color="high"
                    >
                        {localize("landing-feature-2-title")}
                    </Text>
                    <Text
                        style={stylesheet.featureDescription}
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {localize("landing-feature-2-desc")}
                    </Text>
                </View>
            </View>
            <View
                style={[
                    stylesheet.featureCard,
                    {
                        backgroundColor: colors.content.container.default,
                        borderColor: colors.content.border.subtle,
                        width: isMobile ? "100%" : undefined,
                        borderRadius: radiuses.md
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.featureIconContainer,
                        {
                            backgroundColor: colors.system.default.background,
                            borderRadius: radiuses.md
                        }
                    ]}
                >
                    <GraduationCap
                        color={colors.content.icon.default}
                        size={28}
                    />
                </View>
                <View
                    style={stylesheet.featureTextContainer}
                >
                    <Text
                        variant="titleSmallSize"
                        color="high"
                    >
                        {localize("landing-feature-3-title")}
                    </Text>
                    <Text
                        style={stylesheet.featureDescription}
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {localize("landing-feature-3-desc")}
                    </Text>
                </View>
            </View>
        </View>;
    };

    const renderFooter = () => {
        return <View
            style={[
                stylesheet.footerContainer,
                {
                    borderTopColor: colors.content.border.subtle
                }
            ]}
        >
            <Text
                style={stylesheet.footerText}
                variant="bodySmallSize"
                color="mid"
            >
                {`© ${new Date().getFullYear()} ${localize("app-name")}`}
            </Text>
        </View>;
    };

    return <View
        style={[
            stylesheet.container,
            {
                backgroundColor: colors.system.default.background
            }
        ]}
    >
        <View
            style={stylesheet.headerContainer}
        >
            <View
                style={stylesheet.headerLeft}
            />
            <View
                style={stylesheet.headerRight}
            >
                <LocaleSwitcher
                    spreadBehaviour="free"
                />
                <ThemeSwitcher
                    spreadBehaviour="free"
                />
                <UserShortcut
                    actionButtons={isAuthenticated && user?.role !== "guest" ? [
                        {
                            title: localize("dashboard-panel-button" as keyof NCoreUIKit.Translation),
                            onPress: () => {
                                navigation.navigate("AuthenticatedApp" as never);
                            },
                            spreadBehaviour: "stretch",
                            type: "neutral",
                            size: "medium"
                        },
                        {
                            onPress: () => {
                                navigation.navigate("AuthenticatedApp", {
                                    screen: "Profile"
                                });
                            },
                            title: localize("dashboard-profile-button"),
                            spreadBehaviour: "stretch",
                            type: "neutral",
                            size: "medium"
                        }
                    ] : undefined}
                    avatarProps={{
                        title: user?.fullName || user?.name_surname || user?.username,
                        imageUrl: user?.profilePicturePath,
                        size: "small"
                    }}
                    title={user?.fullName || user?.name_surname || user?.username}
                    isSessionActive={isAuthenticated && user?.role !== "guest"}
                    onPress={() => {
                        if (isAuthenticated && !isMobile) {
                            navigation.navigate("AuthenticatedApp", {
                                screen: "Profile"
                            });
                        } else if (!isAuthenticated) {
                            navigation.navigate("Login");
                        }
                    }}
                    variant={isMobile ? "compact" : "default"}
                    isShowLoginButtonWhenNoSession={!isMobile}
                    onLoginPress={() => {
                        navigation.navigate("Login");
                    }}
                    backgroundColor="transparent"
                    isWorkWithCoreComplex={false}
                    onLogoutPress={async () => {
                        await logout();
                    }}
                    spreadBehaviour="free"
                />
            </View>
        </View>
        <ScrollView
            contentContainerStyle={stylesheet.scrollContent}
            showsVerticalScrollIndicator={false}
            bounces={false}
        >
            {renderHero()}
            {renderFeatures()}
            {renderFooter()}
        </ScrollView>
    </View>;
};
export default Landing;
